import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useSelection } from '../context/SelectionContext';

/**
 * Hook to find the first child and semester with unviewed suggestions.
 * Follows the same order as children are displayed in the header (array order from API).
 * For each child, finds the first semester with unviewed suggestions (based on semesters array order).
 */
export function useFirstUnviewedSuggestion() {
  const [firstChild, setFirstChild] = useState(null);
  const [firstSemesterId, setFirstSemesterId] = useState(null);
  const [loading, setLoading] = useState(true);

  const { unviewedCountVersion } = useSelection();

  const findFirstUnviewed = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch all children (same order as header)
      const childrenResponse = await api.get('/parent/children');
      const children = childrenResponse.data.children || [];

      if (children.length === 0) {
        setFirstChild(null);
        setFirstSemesterId(null);
        return;
      }

      // For each child in order, check for unviewed suggestions
      for (const child of children) {
        try {
          // Get unviewed counts by semester for this child
          const unviewedResponse = await api.get(
            `/parent/children/${child.id}/unviewed-suggested-courses`
          );

          const { has_unviewed, counts_by_semester } = unviewedResponse.data;

          if (has_unviewed) {
            // Found a child with unviewed suggestions
            // Now try to find the first semester with unviewed courses

            // If counts_by_semester is missing or empty, return child without semester
            if (!counts_by_semester || Object.keys(counts_by_semester).length === 0) {
              setFirstChild(child);
              setFirstSemesterId(null);
              return;
            }

            // Try to get semesters for this child to know the order
            try {
              const coursesResponse = await api.get(
                `/parent/children/${child.id}/suggested-courses`
              );
              const semesters = coursesResponse.data.semesters || [];

              // Find first semester (in display order) that has unviewed suggestions
              for (const semester of semesters) {
                if (counts_by_semester[semester.id] > 0) {
                  setFirstChild(child);
                  setFirstSemesterId(semester.id);
                  return;
                }
              }

              // If we have counts_by_semester but no matching semester in the list,
              // just use this child with no specific semester
              setFirstChild(child);
              setFirstSemesterId(null);
              return;
            } catch (semesterErr) {
              // Suggested-courses API failed, but we know this child has unviewed
              // Return child without specific semester
              console.error(`Error fetching semesters for child ${child.id}:`, semesterErr);
              setFirstChild(child);
              setFirstSemesterId(null);
              return;
            }
          }
        } catch (err) {
          // Skip this child on unviewed API error, continue to next
          console.error(`Error checking child ${child.id}:`, err);
        }
      }

      // No child with unviewed suggestions found
      setFirstChild(null);
      setFirstSemesterId(null);
    } catch (err) {
      console.error('Error finding first unviewed suggestion:', err);
      setFirstChild(null);
      setFirstSemesterId(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    findFirstUnviewed();
  }, [findFirstUnviewed, unviewedCountVersion]);

  return {
    firstChild,
    firstSemesterId,
    loading,
    refetch: findFirstUnviewed,
  };
}
